import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Unarchive as UnarchiveIcon,
  UploadFile as UploadFileIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import { isConsentFormActive } from '../../constants/ConsentForms';
import { formatFileSize, getConsentFormName } from './consentFormDisplay';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const actionButtonSx = {
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
};

const ConsentFormCard = ({
  consentForm,
  canEdit,
  loading,
  onEdit,
  onUpload,
  onPreview,
  onArchive,
  onRestore,
}) => {
  const { t, i18n } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const active = isConsentFormActive(consentForm);
  const version = consentForm.currentVersion;
  const serviceCount = consentForm.serviceIds.length;

  return (
    <Card
      id={`consent-form-card-${consentForm.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'flex-start',
        width: '100%',
        transition: 'all 0.3s ease',
        opacity: active ? 1 : 0.8,
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 25px rgba(0,0,0,0.15)' },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3, wordBreak: 'break-word' }}>
          {getConsentFormName(consentForm.name, i18n.language, t)}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {serviceCount > 0
            ? t('consentForms.management.card.services', { count: serviceCount })
            : t('consentForms.management.card.noServices')}
        </Typography>

        {version ? (
          <Box sx={{ minWidth: 0, mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
              {`${version.fileName} · ${formatFileSize(version.fileSizeBytes)}`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('consentForms.management.card.uploadedAt', { date: formatUtcDateTime(version.createdAtUtc) })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {`${t('consentForms.management.card.fileHash')}: ${version.sha256.slice(0, 16)}…`}
            </Typography>
          </Box>
        ) : null}

        {consentForm.versions.length > 1 ? (
          <Box>
            <Button
              id={`consent-form-history-${consentForm.id}`}
              size="small"
              color="inherit"
              onClick={() => setHistoryOpen((value) => !value)}
              endIcon={historyOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ px: 0, textTransform: 'none' }}
            >
              {t('consentForms.management.actions.history')}
            </Button>
            <Collapse in={historyOpen}>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {consentForm.versions.map((item) => (
                  <Typography component="li" variant="caption" color="text.secondary" key={item.id} sx={{ display: 'list-item' }}>
                    {`${t('consentForms.management.history.version', { version: item.versionNumber })} · ${formatUtcDateTime(item.createdAtUtc)} · `}
                    {item.id === version?.id
                      ? t('consentForms.management.history.current')
                      : item.requiresReacceptance
                        ? t('consentForms.management.history.reaccept')
                        : t('consentForms.management.history.noReaccept')}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Box>
        ) : null}

        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: canEdit ? 0.75 : 0 }}>
            <Chip
              size="small"
              label={version
                ? t('consentForms.management.card.currentVersion', { version: version.versionNumber })
                : t('consentForms.management.card.noFile')}
              sx={{ backgroundColor: brandAlpha('accent', 0.12), color: BRAND.accentText, fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={consentForm.validityMonths
                ? t('consentForms.management.card.validity', { count: consentForm.validityMonths })
                : t('consentForms.management.card.noExpiry')}
              sx={{ backgroundColor: '#e3f2fd', color: '#1565c0' }}
            />
            <Chip
              size="small"
              label={active ? t('consentForms.management.card.active') : t('consentForms.management.card.archived')}
              sx={{ backgroundColor: active ? '#e8f5e9' : '#ffebee', color: active ? '#2e7d32' : '#c62828' }}
            />
          </Box>

          {canEdit ? (
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              <Tooltip title={t('consentForms.management.actions.edit')}>
                <span>
                  <IconButton
                    id={`consent-form-edit-${consentForm.id}`}
                    size="small"
                    onClick={onEdit}
                    disabled={loading}
                    aria-label={t('consentForms.management.actions.edit')}
                    sx={{ ...actionButtonSx, color: '#1976d2' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={active ? t('consentForms.management.actions.archive') : t('consentForms.management.actions.restore')}>
                <span>
                  <IconButton
                    id={`consent-form-${active ? 'archive' : 'restore'}-${consentForm.id}`}
                    size="small"
                    onClick={active ? onArchive : onRestore}
                    disabled={loading}
                    aria-label={active ? t('consentForms.management.actions.archive') : t('consentForms.management.actions.restore')}
                    sx={{ ...actionButtonSx, color: active ? '#c62828' : '#2e7d32' }}
                  >
                    {active ? <ArchiveIcon fontSize="small" /> : <UnarchiveIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : null}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 1.5, mt: 1.5, borderTop: `1px solid ${BRAND.line}` }}>
          <Button
            id={`consent-form-preview-${consentForm.id}`}
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={onPreview}
            disabled={!version || loading}
            sx={{ color: BRAND.accentText }}
          >
            {t('consentForms.management.actions.preview')}
          </Button>
          {canEdit && active ? (
            <Button
              id={`consent-form-upload-${consentForm.id}`}
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={onUpload}
              disabled={loading}
              sx={{ color: BRAND.accentText, ml: 'auto' }}
            >
              {version ? t('consentForms.management.actions.upload') : t('consentForms.management.actions.uploadFirst')}
            </Button>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConsentFormCard;
